import type { Controls, Dashboard, InstrumentPanel } from 'lib-analytics-types';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { MarkRequired } from 'ts-essentials';

import { AnalyticsState } from 'analytics/analytics.reducer';
import { analyticsApi } from 'apis/analytics';
import { useAppSelector } from 'utils/redux/useAppSelector';

type UseAnalyticsStateResult = Controls &
  MarkRequired<Omit<AnalyticsState, 'context'>, 'dashboard'>;

/**
 * A hook that returns the controls used to request instrument data, as well as
 * other information persisted in the global Redux state.
 *
 * If a panel argument is provided, the controls from the panel are returned if
 * present, instead of the global controls. A panel may have its own unique
 * controls when associated with a quick view or custom dashboard.
 *
 * Similarly if a panel is provided, the dashboard from where the panel
 * originates is returned. Otherwise, the current active dashboard is returned.
 *
 * @param panel The optional instrument panel which may have its own controls.
 */
export const useAnalyticsState = (
  panel?: InstrumentPanel,
): UseAnalyticsStateResult => {
  const state = useAppSelector((state) => state.analytics, shallowEqual);

  /**
   * The dashboard where the panel originates from, if a panel is provided to
   * the hook. Otherwise, the current active global dashboard is returned.
   */
  const { panelDashboard } =
    analyticsApi.endpoints.readDashboards.useQueryState(undefined, {
      skip: !panel,
      selectFromResult: ({ data }) => ({
        panelDashboard: data?.data.dashboards.find(
          ({ id }) => id === panel?.dashboard_id,
        ),
      }),
    });

  const dashboard = useMemo(
    // This hook is only called when a dashboard has been loaded, so we can safely use the non-null
    // assertion operator here to ensure that the dashboard is always defined.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (): Dashboard => panelDashboard ?? state.dashboard!,
    [panelDashboard, state.dashboard],
  );

  /**
   * The current controls used to request instrument data. If the panel has its
   * own unique controls, we use them. Otherwise, we use the global controls.
   */
  const controls = useMemo(
    (): Controls =>
      panel?.controls ?? {
        context: state.context[dashboard.id],
        period: state.period,
        filters: state.filters,
        settings: state.settings,
      },
    [dashboard.id, panel, state],
  );

  return useMemo(
    (): UseAnalyticsStateResult => ({
      ...state,
      ...controls,
      context: controls.context,
      dashboard,
    }),
    [controls, dashboard, state],
  );
};
